/**
 * Advisory Document Service — pushing a file into an engagement's docintel collection.
 *
 * Three HTTP calls through gmid, in this order, each with its own failure mode:
 *
 *   1. `POST /api/v1/documents/docintel-upload`            multipart -> storage_path
 *   2. `POST /api/v1/documents/docintel/{id}/ingest`       202 + job_id
 *   3. `GET  /api/v1/documents/docintel/{id}/ingest-status` poll -> ready | failed
 *
 * **Through gmid, never at docintel.** Deployed docintel is `ingress=internal`, so a
 * browser cannot reach it at all. gmid's eleven `/api/v1/documents/docintel*` routes
 * are the reachable path, and they are the same ones the V3 engagement harness uses.
 *
 * **`conversation_id` is deliberately never sent.** It is gmid-side registry
 * bookkeeping only — docintel has no conversation concept — and gmid answers 409
 * when it names a conversation that does not exist yet. That is exactly our
 * situation: the whole point is to have the document in place BEFORE the first turn
 * creates the conversation. Scoping comes from `collection_id` alone (ADR-014:
 * collection membership is docintel's only scoping model).
 *
 * **Both ids must be UUIDs.** `document_id` is caller-chosen and rejected with 400
 * otherwise — the acceptance set is kept equal to the erasure path's. `collection_id`
 * is rejected with 422. It is also BINDING: ingest attaches the document to exactly
 * the collection named at upload and 409s on a contradicting one, which is what stops
 * the registry naming a collection the document is not in.
 *
 * **Deliberately no client-side type or size gate.** gmid owns the acceptance set —
 * 33 MIME types and a `DOCUMENT_MAX_FILE_SIZE_MB` that is env-configurable — and
 * mirroring either here would be a second source of truth that can only ever
 * disagree. A hardcoded ceiling that drifts LOW is the dangerous direction: it
 * refuses a file the server would have taken, and no error from the server exists
 * to explain it. The 400 and 413 paths below map to precise, non-retryable copy
 * instead, so a rejected file is explained by the side that rejected it. The file
 * input's `accept` attribute is a picker hint only, never validation.
 *
 * **No provider text ever leaves this module.** docintel and gmid both return prose in
 * their error bodies. Every failure surfaces as a fixed code from
 * `advisoryErrorCodes.ts`, which maps to a pre-authored i18n key — the same rule the
 * supervisor SSE `error` event follows (ASVS V7). Detail goes to `console.error` for a
 * developer, never into state.
 */

import { v4 as uuidv4 } from 'uuid';
import {
  ADVISORY_DOCUMENT_INGEST_CODE,
  ADVISORY_DOCUMENT_TIMEOUT_CODE,
  ADVISORY_DOCUMENT_TOO_LARGE_CODE,
  ADVISORY_DOCUMENT_UNSUPPORTED_CODE,
  ADVISORY_DOCUMENT_UPLOAD_CODE,
} from '../redux/slices/advisory/advisoryErrorCodes';
import { ADVISORY_DOCUMENT_ENDPOINTS } from '../redux/slices/advisory/advisoryConfig';

/**
 * Where one file has got to.
 *
 * `ingesting` is its own state and not a sub-state of uploading: the bytes have
 * landed and docintel is running a pipeline on a Cloud Run Job, which for a large
 * scanned PDF means a VLM and minutes of real work. Collapsing the two would make a
 * progress view unable to distinguish "still transferring" from "parsing".
 */
export type AdvisoryDocumentState = 'uploading' | 'ingesting' | 'ready' | 'failed';

/** One file's record. Carries no provider text — see the module docstring. */
export interface AdvisoryDocumentRecord {
  /** Caller-minted UUID. The handle for the ingest and status calls. */
  documentId: string;
  fileName: string;
  sizeBytes: number;
  collectionId: string;
  state: AdvisoryDocumentState;
  /**
   * A fixed code from `advisoryErrorCodes.ts`, or `null`. NEVER a message from
   * gmid or docintel.
   *
   * The CODE rather than the resolved i18n key, so a caller can run it through
   * `mapAdvisoryErrorCode` and get `tone` and `retryable` alongside the key — a
   * rejected file type must not render a retry button that can never succeed.
   */
  errorCode: string | null;
  /** Set when docintel matched these bytes to an already-ingested document. */
  canonicalDocumentId: string | null;
  /** docintel's own counts, when it reported them. Display-only. */
  pageCount: number | null;
  chunkCount: number | null;
}

/**
 * How often to ask, and how long to keep asking.
 *
 * Both mirror the engagement harness, which measured them against real documents.
 * The budget is generous on purpose: a large scanned PDF is routed through a VLM and
 * the harness records ingests that legitimately took minutes. Giving up early would
 * report a failure for a document that was about to succeed — and worse, it would do
 * so while the ingest carried on and eventually landed, leaving the UI permanently
 * disagreeing with the collection.
 */
const POLL_INTERVAL_MS = 5_000;
const INGEST_BUDGET_MS = 900_000;

/** Terminal states. Anything else means keep polling. */
const READY = 'ready';
const FAILED = 'failed';

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

/** A failure carrying a mapped CODE, never provider prose. */
class DocumentIngestError extends Error {
  readonly code: string;

  constructor(code: string, detail: string) {
    // `detail` reaches `console.error` and the developer, never state or copy.
    super(detail);
    this.code = code;
    this.name = 'DocumentIngestError';
  }
}

function uploadFailureCode(status: number): string {
  // 400 and 413 are decided by the file itself and will repeat identically, so they
  // map to non-retryable codes. Everything else may not repeat.
  if (status === 400) {
    return ADVISORY_DOCUMENT_UNSUPPORTED_CODE;
  }
  if (status === 413) {
    return ADVISORY_DOCUMENT_TOO_LARGE_CODE;
  }
  return ADVISORY_DOCUMENT_UPLOAD_CODE;
}

async function readBodyForLog(response: Response): Promise<string> {
  try {
    return (await response.text()).slice(0, 300);
  } catch {
    return '<unreadable body>';
  }
}

export interface IngestAdvisoryDocumentArgs {
  file: File;
  /** The docintel collection to upload into — see `advisoryCollections.ts`. */
  collectionId: string;
  /** A Firebase ID token. gmid resolves the caller from it; no uid is ever sent. */
  token: string;
  signal?: AbortSignal;
  /** Called on every state change, so a caller can render progress per file. */
  onProgress?: (record: AdvisoryDocumentRecord) => void;
}

/**
 * Upload one file, dispatch its ingest, and wait until docintel reports `ready`.
 *
 * Resolves with a terminal record either way — `state: 'failed'` plus an
 * `errorCode` rather than throwing, so a caller rendering a list of files does
 * not have to wrap each one. An `AbortError` is the single exception and propagates,
 * because an aborted upload has no outcome to report.
 *
 * Waiting for `ready` matters and is not politeness: a node reads its collection as
 * it stands when its turn runs, so a document still ingesting when the engagement
 * starts is a document the engagement never sees. The caller is expected to keep the
 * start action closed until every file is terminal.
 */
export async function ingestAdvisoryDocument({
  file,
  collectionId,
  token,
  signal,
  onProgress,
}: IngestAdvisoryDocumentArgs): Promise<AdvisoryDocumentRecord> {
  const record: AdvisoryDocumentRecord = {
    documentId: uuidv4(),
    fileName: file.name,
    sizeBytes: file.size,
    collectionId,
    state: 'uploading',
    errorCode: null,
    canonicalDocumentId: null,
    pageCount: null,
    chunkCount: null,
  };

  const emit = (next: Partial<AdvisoryDocumentRecord>): AdvisoryDocumentRecord => {
    Object.assign(record, next);
    onProgress?.({ ...record });
    return record;
  };

  const authHeaders = { Authorization: `Bearer ${token}` };

  try {
    // ---- 1. upload -------------------------------------------------------
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('document_id', record.documentId);
    form.append('collection_id', collectionId);
    // conversation_id deliberately omitted — see the module docstring.

    const uploadResponse = await fetch(ADVISORY_DOCUMENT_ENDPOINTS.upload, {
      method: 'POST',
      headers: authHeaders,
      body: form,
      signal,
    });
    if (!uploadResponse.ok) {
      throw new DocumentIngestError(
        uploadFailureCode(uploadResponse.status),
        `upload HTTP ${uploadResponse.status}: ${await readBodyForLog(uploadResponse)}`
      );
    }

    // ---- 2. dispatch ingest ---------------------------------------------
    emit({ state: 'ingesting' });
    const ingestResponse = await fetch(ADVISORY_DOCUMENT_ENDPOINTS.ingest(record.documentId), {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      // The body is required, and restates the BINDING collection chosen at upload.
      // gmid 409s on a contradiction rather than silently re-homing the document.
      body: JSON.stringify({ collection_id: collectionId }),
      signal,
    });
    if (!ingestResponse.ok) {
      throw new DocumentIngestError(
        ADVISORY_DOCUMENT_INGEST_CODE,
        `ingest HTTP ${ingestResponse.status}: ${await readBodyForLog(ingestResponse)}`
      );
    }

    // ---- 3. poll to terminal --------------------------------------------
    const deadline = Date.now() + INGEST_BUDGET_MS;
    for (;;) {
      if (Date.now() > deadline) {
        throw new DocumentIngestError(
          ADVISORY_DOCUMENT_TIMEOUT_CODE,
          `ingest did not reach ready within ${INGEST_BUDGET_MS / 1000}s`
        );
      }
      await sleep(POLL_INTERVAL_MS, signal);

      let body: Record<string, unknown> = {};
      try {
        const statusResponse = await fetch(
          ADVISORY_DOCUMENT_ENDPOINTS.ingestStatus(record.documentId),
          {
            headers: authHeaders,
            signal,
          }
        );
        if (!statusResponse.ok) {
          // A status read that does not answer is not an ingest failure — the Job
          // is running either way. Keep polling to the deadline.
          continue;
        }
        const parsed: unknown = await statusResponse.json();
        body = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err;
        }
        // Same reasoning as a non-ok status above.
        continue;
      }

      const state =
        typeof body.state === 'string'
          ? body.state
          : typeof body.status === 'string'
          ? body.status
          : '';
      if (state === FAILED) {
        throw new DocumentIngestError(
          ADVISORY_DOCUMENT_INGEST_CODE,
          `docintel reported failed for ${file.name}`
        );
      }
      if (state === READY) {
        const metadata =
          body.metadata && typeof body.metadata === 'object'
            ? (body.metadata as Record<string, unknown>)
            : {};
        return emit({
          state: 'ready',
          // docintel matches an upload by CONTENT, so re-uploading identical bytes
          // resolves onto the document already ingested. Surfacing it is what makes
          // "why does this say 0 chunks" answerable rather than mysterious.
          canonicalDocumentId:
            typeof body.canonical_document_id === 'string' ? body.canonical_document_id : null,
          pageCount: typeof metadata.pageCount === 'number' ? metadata.pageCount : null,
          chunkCount: typeof metadata.chunkCount === 'number' ? metadata.chunkCount : null,
        });
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err;
    }
    const code = err instanceof DocumentIngestError ? err.code : ADVISORY_DOCUMENT_UPLOAD_CODE;
    // eslint-disable-next-line no-console
    console.error('[advisoryDocumentService]', err instanceof Error ? err.message : err);
    return emit({ state: 'failed', errorCode: code });
  }
}
